import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where, orderBy, limit, Timestamp } from "firebase/firestore";
import toast, { Toaster } from "react-hot-toast";
import { db } from "../firebase";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import { ATTENDANCE_BRANCHES, branchRowForLabel, usesStringIsoTimestamp } from "../config/attendanceBranches";
import { getVisibleAttendanceBranches } from "../config/appBranches";
import { resolveCollectionName } from "../services/attendanceCollections";
import {
  extractEmpKeyFromData,
  extractPrimaryPunchTime,
  pickString,
  pad2,
} from "../helpers/staffDailyHours";
import {
  buildEmployeeCodeLookupForShop,
  extractEmpKeyWithCutover,
} from "../helpers/payrollAttendanceCore";

const MONTH_QUERY_LIMIT = 5000;

function parseExpectedStart(s) {
  const t = String(s ?? "").trim();
  if (!t) return [9, 30];
  const parts = t.split(/[:\s]+/);
  if (parts.length >= 2) {
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (!isNaN(h) && h >= 0 && h <= 23 && !isNaN(m) && m >= 0 && m <= 59) return [h, m];
  }
  return [9, 30];
}

function isLateFirstPunch(first, lateH, lateM) {
  return first.getHours() > lateH || (first.getHours() === lateH && first.getMinutes() > lateM);
}

function ymdKey(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

async function fetchMonthPunchDocs(punchCollection, monthStart, monthEnd, preferString) {
  const tryGet = async (q) => {
    try {
      return await getDocs(q);
    } catch {
      return null;
    }
  };

  const startStr = `${monthStart.getFullYear()}-${pad2(monthStart.getMonth() + 1)}-${pad2(monthStart.getDate())}T00:00:00`;
  const endStr = `${monthEnd.getFullYear()}-${pad2(monthEnd.getMonth() + 1)}-${pad2(monthEnd.getDate())}T00:00:00`;

  const strQ = () =>
    query(
      collection(db, punchCollection),
      where("timestamp", ">=", startStr),
      where("timestamp", "<", endStr),
      orderBy("timestamp"),
      limit(MONTH_QUERY_LIMIT)
    );

  const tsQ = () =>
    query(
      collection(db, punchCollection),
      where("timestamp", ">=", Timestamp.fromDate(monthStart)),
      where("timestamp", "<", Timestamp.fromDate(monthEnd)),
      orderBy("timestamp"),
      limit(MONTH_QUERY_LIMIT)
    );

  const order = preferString ? [strQ, tsQ] : [tsQ, strQ];
  for (const build of order) {
    const snap = await tryGet(build());
    if (snap && !snap.empty) return { docs: snap.docs, mode: "range" };
  }

  const fallback = await tryGet(query(collection(db, punchCollection), limit(MONTH_QUERY_LIMIT)));
  if (!fallback) return { docs: [], mode: "none" };
  const ms0 = monthStart.getTime();
  const ms1 = monthEnd.getTime();
  const docs = fallback.docs.filter((d) => {
    const dt = extractPrimaryPunchTime(d.data() || {});
    if (!dt) return false;
    const t = dt.getTime();
    return t >= ms0 && t < ms1;
  });
  return { docs, mode: "client-filter" };
}

export default function DailyLateReportPage() {
  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const visibleBranches = useMemo(() => getVisibleAttendanceBranches(storedUser), [storedUser]);

  const [selectedShopLabel, setSelectedShopLabel] = useState(ATTENDANCE_BRANCHES[0].label);
  const [empCollectionName, setEmpCollectionName] = useState(null);
  const [punchCollectionName, setPunchCollectionName] = useState(null);

  const [employees, setEmployees] = useState([]);
  const [employeeCodeLookup, setEmployeeCodeLookup] = useState(() => new Map());
  const [selectedEmpId, setSelectedEmpId] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingPunches, setLoadingPunches] = useState(false);
  const [dayRows, setDayRows] = useState([]);
  const [detailDay, setDetailDay] = useState(null);
  const [hasGenerated, setHasGenerated] = useState(false);

  useEffect(() => {
    if (!visibleBranches.length) return;
    const allowed = new Set(visibleBranches.map((b) => b.label));
    if (!allowed.has(selectedShopLabel)) {
      setSelectedShopLabel(visibleBranches[0].label);
    }
  }, [visibleBranches, selectedShopLabel]);

  const resolveBranches = useCallback(async () => {
    const row = branchRowForLabel(selectedShopLabel);
    const [ec, pc] = await Promise.all([
      resolveCollectionName(row.employeeCandidates || []),
      resolveCollectionName(row.punchCandidates || []),
    ]);
    setEmpCollectionName(ec);
    setPunchCollectionName(pc);
  }, [selectedShopLabel]);

  useEffect(() => {
    resolveBranches();
  }, [resolveBranches]);

  const loadEmployees = useCallback(async () => {
    if (!empCollectionName) return;
    setLoadingEmployees(true);
    try {
      const snap = await getDocs(collection(db, empCollectionName));
      const list = [];
      snap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const status = String(data.Status ?? data.status ?? "")
          .trim()
          .toLowerCase();
        if (status === "left") return;
        const id = extractEmpKeyFromData(data, docSnap.id);
        const name =
          pickString(data, ["EmployeeName", "employeeName", "Employee", "name", "fullName"]) || id;
        const expectedStart = String(data.expectedStartTime ?? "09:30").trim() || "09:30";
        const lunchMinutes = parseInt(String(data.lunchMinutes ?? 60), 10);
        list.push({
          id,
          name,
          expectedStart,
          lunchMinutes: Number.isFinite(lunchMinutes) ? lunchMinutes : 60,
        });
      });
      list.sort((a, b) => a.name.localeCompare(b.name));
      setEmployees(list);
      try {
        setEmployeeCodeLookup(buildEmployeeCodeLookupForShop({ label: selectedShopLabel }, snap.docs));
      } catch {
        setEmployeeCodeLookup(new Map());
      }
      setSelectedEmpId("");
      setDayRows([]);
      setHasGenerated(false);
    } catch (e) {
      console.error(e);
      toast.error(`Failed to load employees: ${e?.message || e}`);
      setEmployees([]);
      setEmployeeCodeLookup(new Map());
    } finally {
      setLoadingEmployees(false);
    }
  }, [empCollectionName, selectedShopLabel]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const selectedEmpMeta = useMemo(
    () => employees.find((e) => e.id === selectedEmpId),
    [employees, selectedEmpId]
  );

  const generate = async () => {
    if (!selectedEmpId) {
      toast.error("Please select an employee");
      return;
    }
    if (!punchCollectionName) {
      toast.error("Punch collection not ready");
      return;
    }
    setLoadingPunches(true);
    setDayRows([]);
    try {
      const y = selectedMonth.getFullYear();
      const m = selectedMonth.getMonth();
      const monthStart = new Date(y, m, 1, 0, 0, 0, 0);
      const monthEnd = new Date(y, m + 1, 1, 0, 0, 0, 0);

      const preferString = usesStringIsoTimestamp(punchCollectionName);
      const { docs } = await fetchMonthPunchDocs(punchCollectionName, monthStart, monthEnd, preferString);

      const byDay = new Map();
      for (const d of docs) {
        const raw = d.data() || {};
        const dt = extractPrimaryPunchTime(raw);
        if (!dt) continue;

        const empId = extractEmpKeyWithCutover(raw, d.id, dt, employeeCodeLookup);
        if (!empId) continue;
        if (String(empId) !== String(selectedEmpId)) continue;

        const key = ymdKey(new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()));
        if (!byDay.has(key)) byDay.set(key, []);
        byDay.get(key).push(dt);
      }

      const expectedStr = selectedEmpMeta?.expectedStart ?? "09:30";
      const [lateH, lateM] = parseExpectedStart(expectedStr);

      const list = [];
      for (const [key, times] of byDay) {
        const parts = key.split("-").map(Number);
        if (parts.length !== 3) continue;
        const date = new Date(parts[0], parts[1] - 1, parts[2]);
        times.sort((a, b) => a.getTime() - b.getTime());
        const first = times[0];
        const late = times.length > 0 && isLateFirstPunch(first, lateH, lateM);
        list.push({
          date,
          key,
          punches: times,
          isLate: late,
          firstPunchFormatted: times.length
            ? first.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true })
            : "—",
        });
      }
      list.sort((a, b) => a.date - b.date);
      setDayRows(list);
      setHasGenerated(true);
    } catch (e) {
      console.error(e);
      toast.error(`Error: ${e?.message || e}`);
      setHasGenerated(true);
    } finally {
      setLoadingPunches(false);
    }
  };

  const changeMonth = (delta) => {
    setSelectedMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
    setDayRows([]);
    setHasGenerated(false);
  };

  const lateCount = dayRows.filter((d) => d.isLate).length;

  return (
    <div className="min-h-screen bg-gray-100">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="max-w-2xl mx-auto p-4 pb-10">
        <div className="bg-teal-600 text-white rounded-t-xl px-4 py-3 flex flex-wrap items-center gap-2">
          <h1 className="text-base font-extrabold tracking-tight">Daily Late Report</h1>
          <select
            value={selectedShopLabel}
            onChange={(e) => {
              setSelectedShopLabel(e.target.value);
              setDayRows([]);
              setHasGenerated(false);
            }}
            className="text-sm text-black bg-white rounded px-2 py-1 ml-auto max-w-[200px]"
          >
            {(visibleBranches.length ? visibleBranches : ATTENDANCE_BRANCHES).map((b) => (
              <option key={b.label} value={b.label}>
                {b.label}
              </option>
            ))}
          </select>
        </div>

        <div className="bg-white rounded-b-xl shadow border border-t-0 border-gray-200 p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Employee</label>
              <select
                value={selectedEmpId}
                onChange={(e) => {
                  setSelectedEmpId(e.target.value);
                  setDayRows([]);
                  setHasGenerated(false);
                }}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                disabled={loadingEmployees || !empCollectionName}
              >
                <option value="">Select</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name} ({e.id})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Month</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-2 py-2 border rounded-lg text-sm"
                  onClick={() => changeMonth(-1)}
                >
                  ‹
                </button>
                <input
                  type="month"
                  className="flex-1 border rounded-lg px-2 py-2 text-sm"
                  value={`${selectedMonth.getFullYear()}-${pad2(selectedMonth.getMonth() + 1)}`}
                  onChange={(e) => {
                    const [y, mo] = e.target.value.split("-").map(Number);
                    setSelectedMonth(new Date(y, mo - 1, 1));
                    setDayRows([]);
                    setHasGenerated(false);
                  }}
                />
                <button
                  type="button"
                  className="px-2 py-2 border rounded-lg text-sm"
                  onClick={() => changeMonth(1)}
                >
                  ›
                </button>
              </div>
            </div>
          </div>

          {selectedEmpMeta && (
            <p className="text-[11px] text-gray-500">
              Expected start: {selectedEmpMeta.expectedStart} · Lunch: {selectedEmpMeta.lunchMinutes} min
            </p>
          )}

          {loadingEmployees && <div className="h-1 bg-teal-100 rounded overflow-hidden"><div className="h-full bg-teal-500 animate-pulse w-1/2" /></div>}

          <button
            type="button"
            disabled={loadingPunches || loadingEmployees || !empCollectionName}
            onClick={generate}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-teal-600 text-white py-3 text-sm font-semibold disabled:opacity-50"
          >
            {loadingPunches ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Loading…
              </>
            ) : (
              <>
                <span aria-hidden>📋</span> Generate punch list
              </>
            )}
          </button>

          {dayRows.length > 0 && (
            <div className="flex items-center gap-2 text-orange-800 font-bold text-sm px-1">
              <span aria-hidden>⚠</span>
              Total late this month: {lateCount}
            </div>
          )}

          {!loadingPunches && dayRows.length === 0 && (
            <p className="text-center text-sm text-gray-500 py-8">
              {!hasGenerated
                ? "Select employee and month, then tap Generate"
                : "No punches for this month"}
            </p>
          )}

          {dayRows.length > 0 && (
            <ul className="space-y-2">
              {dayRows.map((day) => (
                <li key={day.key}>
                  <button
                    type="button"
                    onClick={() => setDetailDay(day)}
                    className="w-full text-left border border-gray-200 rounded-lg p-3 hover:bg-gray-50 flex items-start justify-between gap-2"
                  >
                    <div>
                      <div className="font-semibold text-gray-900">
                        {day.date.toLocaleDateString("en-IN", {
                          weekday: "long",
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {day.punches.length} punch(es) · First: {day.firstPunchFormatted}
                        {day.isLate ? " · Late" : ""}
                      </div>
                    </div>
                    {day.isLate ? (
                      <span className="shrink-0 text-[11px] font-bold bg-orange-500 text-white px-2 py-0.5 rounded-full">
                        Late
                      </span>
                    ) : (
                      <span className="text-gray-400">›</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <p className="text-[10px] text-gray-500 mt-2 px-1">
          Late = first punch after expected start ({selectedEmpMeta?.expectedStart ?? "09:30"}). Collections: «
          {punchCollectionName || "—"}», «{empCollectionName || "—"}».
        </p>
      </main>

      {detailDay && (
        <div
          className="fixed inset-0 z-[1200] bg-black/40 flex items-end sm:items-center justify-center p-2"
          onClick={() => setDetailDay(null)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-xl w-full max-w-md max-h-[75vh] overflow-hidden flex flex-col shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900">
                Punch list —{" "}
                {detailDay.date.toLocaleDateString("en-IN", {
                  weekday: "long",
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </h2>
            </div>
            <ul className="overflow-y-auto p-2">
              {detailDay.punches.map((t, i) => (
                <li
                  key={`${t.getTime()}-${i}`}
                  className="flex items-center gap-3 px-3 py-2 border-b border-gray-50"
                >
                  <span className="w-8 h-8 rounded-full bg-teal-100 text-teal-800 font-bold text-xs flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span className="font-semibold text-gray-900">
                    {t.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true })}
                  </span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="m-3 py-2 rounded-lg border border-gray-200 text-sm font-medium"
              onClick={() => setDetailDay(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
